import type {
  AnalyticsDevice,
  AnalyticsErrorCategory,
  AnalyticsLanguage,
  AnalyticsMeasureCapture,
  AnalyticsMeasurementSample,
  AnalyticsNavigationKind,
  AnalyticsPageKey,
  AnalyticsSource,
} from '@omaplus/shared-types';

import type { AnalyticsCapturePort } from './AnalyticsCapturePort.js';

const ACTIVE_SEGMENT_MS = 15_000;
const MAX_CONTINUOUS_INTERVAL_MS = 60_000;
const MAX_BUFFERED_SAMPLES = 128;
const MAX_DELIVERY_AGE_MS = 120_000;
const MAX_BATCH_SAMPLES = 32;
const MAX_BATCH_BYTES = 64 * 1024;
const MAX_MEASURE_ATTEMPTS = 3;
const ERROR_CATEGORIES = new Set<AnalyticsErrorCategory>([
  'render',
  'routeLoad',
  'resourceLoad',
  'unhandled',
]);

export interface DisplayedPage {
  readonly navigationId: string;
  readonly pageKey: AnalyticsPageKey;
  readonly navigationKind: AnalyticsNavigationKind;
  readonly device: AnalyticsDevice;
  readonly source: AnalyticsSource;
  /** Sanitized at browser entry; never raw URLs or query values. */
  readonly referrerHost?: string | null;
  readonly campaignSource?: string | null;
  readonly campaignMedium?: string | null;
  readonly campaignName?: string | null;
  readonly language: AnalyticsLanguage;
  readonly load?: Readonly<{ kind: 'document' | 'route'; durationMs: number }>;
}

export interface CaptureController {
  pageDisplayed(page: DisplayedPage): void;
  navigationStarted(navigationId: string): void;
  documentLoadObserved(durationMs: number): void;
  pageNoLongerDisplayed(navigationId: string): void;
  pageHidden(): void;
  errorObserved(category: AnalyticsErrorCategory): void;
  visibilityChanged(state: Readonly<{ visible: boolean; focused: boolean }>): void;
  tick(): void;
  accountGenerationChanged(): void;
  dispose(): void;
}

export interface CreateCaptureControllerOptions {
  readonly port: AnalyticsCapturePort;
  /** A monotonic browser-relative time source, normally `performance.now()`. */
  readonly now: () => number;
  readonly uuid: () => string;
}

interface Calibration {
  readonly monotonicAtAcknowledgement: number;
  readonly serverEpochMs: number;
}

interface PendingSample {
  readonly sample: AnalyticsMeasurementSample;
  readonly createdAt: number;
  inFlight: boolean;
}

type MeasurementBody =
  | Readonly<{ kind: 'active'; from: string; to: string }>
  | Readonly<{ kind: 'load'; loadKind: 'document' | 'route'; durationMs: number }>
  | Readonly<{
      kind: 'error';
      occurredAt: string;
      category: 'render' | 'routeLoad' | 'resourceLoad' | 'unhandled';
    }>
  | Readonly<{ kind: 'closed'; occurredAt: string; finalSequence: number; lossObserved: boolean }>;

interface CapturedPage {
  readonly navigationId: string;
  readonly pageViewId: string;
  readonly generation: number;
  readonly acknowledgementStartedAt: number;
  readonly page: DisplayedPage;
  pending: PendingSample[];
  acknowledged: boolean;
  acknowledgementFinished: boolean;
  delivering: boolean;
  closed: boolean;
  lossObserved: boolean;
  nextSequence: number;
  activeStartedAt: number | null;
  calibration: Calibration | null;
  acknowledgedOccurredAt: string | null;
  terminalAt: number | null;
  terminalQueued: boolean;
  documentLoadRecorded: boolean;
  pendingDocumentLoadDurationMs: number | null;
}

function encodedBytes(input: AnalyticsMeasureCapture): number {
  return new TextEncoder().encode(JSON.stringify(input)).byteLength;
}

/**
 * Memory-only capture state machine. Browser listeners and router readiness feed
 * this object; it deliberately knows neither routes nor session identity.
 */
class CaptureControllerImpl implements CaptureController {
  readonly #port: AnalyticsCapturePort;
  readonly #now: () => number;
  readonly #uuid: () => string;
  #generation = 0;
  #visible = true;
  #focused = true;
  #displayed = false;
  #resumeAttempt = 0;
  #current: CapturedPage | null = null;
  readonly #pagesByNavigation = new Map<string, CapturedPage>();

  constructor({ port, now, uuid }: CreateCaptureControllerOptions) {
    this.#port = port;
    this.#now = now;
    this.#uuid = uuid;
  }

  pageDisplayed(page: DisplayedPage): void {
    const existing = this.#pagesByNavigation.get(page.navigationId);
    if (existing !== undefined) {
      const wasActive = this.#isActive();
      this.#displayed = true;
      if (!wasActive && this.#isActive() && existing === this.#current && !existing.closed) {
        this.#resumeAfterAvailabilityCheck();
      }
      return;
    }

    const previous = this.#current;
    this.#closeCurrent();
    const captured: CapturedPage = {
      navigationId: page.navigationId,
      pageViewId: this.#uuid(),
      generation: this.#generation,
      acknowledgementStartedAt: this.#now(),
      page,
      pending: [],
      acknowledged: false,
      acknowledgementFinished: false,
      delivering: false,
      closed: false,
      lossObserved: false,
      nextSequence: 1,
      activeStartedAt: null,
      calibration: null,
      acknowledgedOccurredAt: null,
      terminalAt: null,
      terminalQueued: false,
      documentLoadRecorded: false,
      pendingDocumentLoadDurationMs: null,
    };
    this.#pagesByNavigation.set(page.navigationId, captured);
    this.#current = captured;
    this.#displayed = true;
    if (previous !== null) {
      this.#cleanup(previous);
    }
    void this.#acknowledgePage(captured);
  }

  navigationStarted(_navigationId: string): void {
    // A candidate is deliberately inert. Only pageDisplayed commits a view.
  }

  documentLoadObserved(durationMs: number): void {
    const page = this.#current;
    if (
      page === null ||
      page.closed ||
      !this.#isCurrentGeneration(page) ||
      page.page.navigationKind !== 'document' ||
      page.documentLoadRecorded ||
      page.pendingDocumentLoadDurationMs !== null ||
      !this.#isValidLoadDuration(durationMs)
    ) {
      return;
    }
    if (!page.acknowledged) {
      page.pendingDocumentLoadDurationMs = durationMs;
      return;
    }
    page.documentLoadRecorded = true;
    this.#appendLoad(page, 'document', durationMs);
  }

  pageNoLongerDisplayed(navigationId: string): void {
    const page = this.#current;
    if (page === null || page.navigationId !== navigationId || page.closed || !this.#displayed) {
      return;
    }
    const wasActive = this.#isActive();
    this.#displayed = false;
    if (wasActive) {
      this.#resumeAttempt += 1;
      this.#endActiveInterval();
    }
  }

  pageHidden(): void {
    this.#closeCurrent(true);
    this.#current = null;
    this.#displayed = false;
  }

  errorObserved(category: AnalyticsErrorCategory): void {
    if (!isErrorCategory(category)) {
      return;
    }
    const page = this.#current;
    if (page === null || !page.acknowledged || page.closed || !this.#isCurrentGeneration(page)) {
      return;
    }
    this.#appendSample(page, {
      kind: 'error',
      occurredAt: this.#toServerIso(page, this.#now()),
      category,
    });
  }

  visibilityChanged(state: Readonly<{ visible: boolean; focused: boolean }>): void {
    const wasActive = this.#isActive();
    this.#visible = state.visible;
    this.#focused = state.focused;
    const isActive = this.#isActive();

    if (wasActive && !isActive) {
      this.#resumeAttempt += 1;
      this.#endActiveInterval();
      return;
    }
    if (!wasActive && isActive) {
      this.#resumeAfterAvailabilityCheck();
    }
  }

  tick(): void {
    const page = this.#current;
    if (page === null || !this.#isActive() || !page.acknowledged || page.activeStartedAt === null) {
      return;
    }
    const now = this.#now();
    const duration = now - page.activeStartedAt;
    if (duration < ACTIVE_SEGMENT_MS) {
      return;
    }
    if (duration > MAX_CONTINUOUS_INTERVAL_MS) {
      page.lossObserved = true;
      page.activeStartedAt = now;
      return;
    }
    this.#appendActive(page, page.activeStartedAt, now);
    page.activeStartedAt = now;
  }

  accountGenerationChanged(): void {
    this.#generation += 1;
    this.#current = null;
    this.#displayed = false;
    this.#pagesByNavigation.clear();
  }

  dispose(): void {
    this.accountGenerationChanged();
  }

  async #acknowledgePage(page: CapturedPage): Promise<void> {
    let acknowledgement: Awaited<ReturnType<AnalyticsCapturePort['pageView']>> | null = null;
    for (let attempt = 0; attempt < MAX_MEASURE_ATTEMPTS; attempt += 1) {
      if (!this.#isCurrentGeneration(page) || !this.#isAcknowledgementFresh(page)) {
        page.lossObserved = true;
        this.#finishAcknowledgement(page);
        return;
      }
      try {
        acknowledgement = await this.#port.pageView({
          pageViewId: page.pageViewId,
          pageKey: page.page.pageKey,
          navigationKind: page.page.navigationKind,
          device: page.page.device,
          source: page.page.source,
          ...(page.page.referrerHost === undefined ? {} : { referrerHost: page.page.referrerHost }),
          ...(page.page.campaignSource === undefined
            ? {}
            : { campaignSource: page.page.campaignSource }),
          ...(page.page.campaignMedium === undefined
            ? {}
            : { campaignMedium: page.page.campaignMedium }),
          ...(page.page.campaignName === undefined ? {} : { campaignName: page.page.campaignName }),
          language: page.page.language,
        });
        break;
      } catch {
        // The logical page id is intentionally reused for a bounded retry.
        if (!this.#isCurrentGeneration(page) || !this.#isAcknowledgementFresh(page)) {
          page.lossObserved = true;
          this.#finishAcknowledgement(page);
          return;
        }
      }
    }
    if (acknowledgement === null) {
      if (this.#isCurrentGeneration(page)) {
        page.lossObserved = true;
      }
      this.#finishAcknowledgement(page);
      return;
    }
    try {
      const currentGeneration = this.#isCurrentGeneration(page);
      if (!currentGeneration || acknowledgement.pageViewId !== page.pageViewId) {
        if (currentGeneration) {
          page.lossObserved = true;
        }
        this.#finishAcknowledgement(page);
        return;
      }
      const serverEpochMs = Date.parse(acknowledgement.serverNow);
      if (!Number.isFinite(serverEpochMs)) {
        page.lossObserved = true;
        this.#finishAcknowledgement(page);
        return;
      }
      page.acknowledged = true;
      page.acknowledgedOccurredAt = acknowledgement.occurredAt;
      page.calibration = { monotonicAtAcknowledgement: this.#now(), serverEpochMs };
      if (page.page.load !== undefined) {
        if (page.page.load.kind === 'document') {
          page.documentLoadRecorded = true;
          page.pendingDocumentLoadDurationMs = null;
        }
        this.#appendLoad(page, page.page.load.kind, page.page.load.durationMs, page.closed);
      } else if (page.pendingDocumentLoadDurationMs !== null) {
        const durationMs = page.pendingDocumentLoadDurationMs;
        page.pendingDocumentLoadDurationMs = null;
        page.documentLoadRecorded = true;
        this.#appendLoad(page, 'document', durationMs, page.closed);
      }
      if (page.closed) {
        this.#queueTerminal(page);
      } else if (this.#current === page && this.#isActive()) {
        page.activeStartedAt = this.#now();
      }
      this.#deliver(page);
      this.#finishAcknowledgement(page);
    } catch {
      // Best-effort analytics never blocks the member surface. A malformed ACK
      // means no measurements may be sent for this logical page.
      if (this.#isCurrentGeneration(page)) {
        page.lossObserved = true;
      }
      this.#finishAcknowledgement(page);
    }
  }

  #resumeAfterAvailabilityCheck(): void {
    const page = this.#current;
    if (page === null || !page.acknowledged || page.closed) {
      return;
    }
    const generation = this.#generation;
    const resumeAttempt = ++this.#resumeAttempt;
    void this.#port
      .availability({})
      .then((availability) => {
        if (
          generation !== this.#generation ||
          this.#current !== page ||
          resumeAttempt !== this.#resumeAttempt ||
          !this.#isActive() ||
          availability.status !== 'ready'
        ) {
          return;
        }
        const serverEpochMs = Date.parse(availability.serverNow);
        if (!Number.isFinite(serverEpochMs)) {
          page.lossObserved = true;
          return;
        }
        page.calibration = { monotonicAtAcknowledgement: this.#now(), serverEpochMs };
        page.activeStartedAt = this.#now();
      })
      .catch(() => {
        if (generation === this.#generation && this.#current === page) {
          page.lossObserved = true;
        }
      });
  }

  #closeCurrent(recordZeroInterval = false): void {
    const page = this.#current;
    if (page === null || page.closed) {
      return;
    }
    this.#endActiveInterval(recordZeroInterval);
    page.closed = true;
    page.terminalAt = this.#now();
    this.#queueTerminal(page);
  }

  #queueTerminal(page: CapturedPage): void {
    if (!page.acknowledged || page.terminalAt === null || page.terminalQueued) {
      return;
    }
    const occurredAt = this.#toServerIso(page, page.terminalAt);
    const parentOccurredAt = Date.parse(page.acknowledgedOccurredAt ?? '');
    if (!Number.isFinite(parentOccurredAt) || Date.parse(occurredAt) < parentOccurredAt) {
      page.lossObserved = true;
      return;
    }
    page.terminalQueued = true;
    this.#appendSample(
      page,
      {
        kind: 'closed',
        occurredAt,
        finalSequence: page.nextSequence,
        lossObserved: page.lossObserved,
      },
      true,
    );
    this.#deliver(page);
  }

  #endActiveInterval(recordZeroInterval = false): void {
    const page = this.#current;
    if (page === null || !page.acknowledged || page.activeStartedAt === null) {
      return;
    }
    const now = this.#now();
    const duration = now - page.activeStartedAt;
    page.activeStartedAt = null;
    if (duration > MAX_CONTINUOUS_INTERVAL_MS) {
      page.lossObserved = true;
      return;
    }
    if (duration > 0 || (duration === 0 && recordZeroInterval)) {
      this.#appendActive(page, now - duration, now);
    }
  }

  #appendActive(page: CapturedPage, from: number, to: number): void {
    this.#appendSample(page, {
      kind: 'active',
      from: this.#toServerIso(page, from),
      to: this.#toServerIso(page, to),
    });
  }

  #appendLoad(
    page: CapturedPage,
    loadKind: 'document' | 'route',
    durationMs: number,
    allowClosed = false,
  ): void {
    if (!this.#isValidLoadDuration(durationMs)) {
      page.lossObserved = true;
      return;
    }
    this.#appendSample(page, { kind: 'load', loadKind, durationMs }, allowClosed);
  }

  #isValidLoadDuration(durationMs: number): boolean {
    return (
      Number.isFinite(durationMs) && durationMs >= 0 && durationMs <= MAX_CONTINUOUS_INTERVAL_MS
    );
  }

  #appendSample(page: CapturedPage, body: MeasurementBody, allowClosed = false): void {
    if (!page.acknowledged || (page.closed && !allowClosed)) {
      return;
    }
    const sample = {
      ...body,
      observationId: this.#uuid(),
      pageViewId: page.pageViewId,
      sequence: page.nextSequence,
    } as AnalyticsMeasurementSample;
    page.nextSequence += 1;
    if (!this.#reservePendingSample()) {
      page.lossObserved = true;
      return;
    }
    page.pending.push({ sample, createdAt: this.#now(), inFlight: false });
    this.#deliver(page);
  }

  #deliver(page: CapturedPage): void {
    if (!page.acknowledged || page.delivering || !this.#isCurrentGeneration(page)) {
      return;
    }
    const delivery = this.#deliverPending(page);
    page.delivering = true;
    void delivery.finally(() => {
      page.delivering = false;
      if (page.pending.length > 0) {
        this.#deliver(page);
      }
      this.#cleanup(page);
    });
  }

  async #deliverPending(page: CapturedPage): Promise<void> {
    while (page.pending.length > 0 && this.#isCurrentGeneration(page)) {
      this.#discardExpired(page);
      const batch = this.#nextBatch(page);
      if (batch.length === 0) {
        return;
      }
      const input = { samples: batch.map((pending) => pending.sample) };
      for (const pending of batch) {
        pending.inFlight = true;
      }
      let outcomes:
        | readonly {
            readonly status: 'accepted' | 'duplicate' | 'rejected';
            readonly observationId: string | null;
          }[]
        | null = null;
      for (let attempt = 0; attempt < MAX_MEASURE_ATTEMPTS; attempt += 1) {
        if (!this.#isCurrentGeneration(page) || !this.#isBatchFresh(batch)) {
          break;
        }
        try {
          outcomes = await this.#port.measure(input);
          break;
        } catch {
          // Retry at most twice after the initial attempt. A failed delivery is
          // represented as known loss; it never changes retained sequences.
        }
      }
      if (!this.#isCurrentGeneration(page)) {
        return;
      }
      const delivered = new Set(batch);
      page.pending = page.pending.filter((pending) => !delivered.has(pending));
      if (outcomes === null || outcomes.some((outcome) => outcome.status === 'rejected')) {
        page.lossObserved = true;
      }
    }
  }

  #discardExpired(page: CapturedPage): void {
    const oldestAllowed = this.#now() - MAX_DELIVERY_AGE_MS;
    const before = page.pending.length;
    page.pending = page.pending.filter(
      (pending) => pending.inFlight || pending.createdAt >= oldestAllowed,
    );
    if (page.pending.length !== before) {
      page.lossObserved = true;
    }
  }

  #nextBatch(page: CapturedPage): readonly PendingSample[] {
    const batch: PendingSample[] = [];
    for (const pending of page.pending) {
      if (batch.length === MAX_BATCH_SAMPLES) {
        break;
      }
      const candidate = [...batch, pending];
      if (encodedBytes({ samples: candidate.map(({ sample }) => sample) }) > MAX_BATCH_BYTES) {
        if (batch.length === 0) {
          page.lossObserved = true;
          page.pending = page.pending.filter((item) => item !== pending);
        }
        break;
      }
      batch.push(pending);
    }
    return batch;
  }

  #toServerIso(page: CapturedPage, monotonicTime: number): string {
    const calibration = page.calibration;
    if (calibration === null) {
      throw new Error('Measurement was built before page acknowledgement');
    }
    return new Date(
      calibration.serverEpochMs + monotonicTime - calibration.monotonicAtAcknowledgement,
    ).toISOString();
  }

  #isCurrentGeneration(page: CapturedPage): boolean {
    return page.generation === this.#generation;
  }

  #isAcknowledgementFresh(page: CapturedPage): boolean {
    return this.#now() - page.acknowledgementStartedAt <= MAX_DELIVERY_AGE_MS;
  }

  #isBatchFresh(batch: readonly PendingSample[]): boolean {
    return batch.every((pending) => this.#now() - pending.createdAt <= MAX_DELIVERY_AGE_MS);
  }

  #reservePendingSample(): boolean {
    while (this.#pendingSampleCount() >= MAX_BUFFERED_SAMPLES) {
      const oldest = this.#oldestDroppableSample();
      if (oldest === null) {
        return false;
      }
      oldest.page.pending = oldest.page.pending.filter((pending) => pending !== oldest.sample);
      oldest.page.lossObserved = true;
      this.#cleanup(oldest.page);
    }
    return true;
  }

  #pendingSampleCount(): number {
    let count = 0;
    for (const page of this.#pagesByNavigation.values()) {
      count += page.pending.length;
    }
    return count;
  }

  #oldestDroppableSample(): Readonly<{ page: CapturedPage; sample: PendingSample }> | null {
    let oldest: Readonly<{ page: CapturedPage; sample: PendingSample }> | null = null;
    for (const page of this.#pagesByNavigation.values()) {
      for (const sample of page.pending) {
        if (!sample.inFlight && (oldest === null || sample.createdAt < oldest.sample.createdAt)) {
          oldest = { page, sample };
        }
      }
    }
    return oldest;
  }

  #cleanup(page: CapturedPage): void {
    if (
      this.#pagesByNavigation.get(page.navigationId) === page &&
      page !== this.#current &&
      page.closed &&
      page.acknowledgementFinished &&
      !page.delivering &&
      page.pending.length === 0
    ) {
      this.#pagesByNavigation.delete(page.navigationId);
    }
  }

  #finishAcknowledgement(page: CapturedPage): void {
    page.acknowledgementFinished = true;
    this.#cleanup(page);
  }

  #isActive(): boolean {
    return this.#visible && this.#focused && this.#displayed;
  }
}

function isErrorCategory(category: AnalyticsErrorCategory): boolean {
  return ERROR_CATEGORIES.has(category);
}

export function createCaptureController(
  options: CreateCaptureControllerOptions,
): CaptureController {
  return new CaptureControllerImpl(options);
}
